import * as Yup from "yup";

/**
 * Shared Yup rules for IRS-form editors, mirroring the backend field validators
 * (apps/forms mappers' `validate_data` / `_poa_defaults`) so bad input surfaces
 * as an inline field error instead of a generic 400 toast on save/generate.
 */

// Accepts: a year (2021), a range (2021-2023), a quarter (Q1 2020) or quarter
// range (Q1 2020-Q4 2023), "N/A"/"Not Applicable", a comma-separated list of
// those, or blank. Matches backend `is_valid_year_range`.
// Mirrors the backend `_YEAR_TOKEN_RE` (apps/forms/mappings/_poa_defaults.py)
// 1:1, including the case-insensitive flag and `Not\s+Applicable` whitespace.
const YEAR_PART =
  /^\s*(?:N\/?A|Not\s+Applicable|[Qq][1-4]\s*(?:19|20)\d{2}(?:\s*-\s*[Qq][1-4]\s*(?:19|20)\d{2})?|(?:19|20)\d{2}(?:\s*-\s*(?:19|20)\d{2})?)\s*$/i;

export const YEAR_RANGE_MESSAGE =
  "Enter a year, range (2021-2023), quarter (Q1 2022), or N/A";

/** True if a value is blank or a valid (comma-list of) year/range/quarter/N/A. */
export const isYearRange = (v: string | undefined | null): boolean =>
  !v ? true : v.split(",").every((part) => !part.trim() || YEAR_PART.test(part));

/** Year / tax-period field (blank allowed; comma-lists allowed). */
export const yearRange = Yup.string()
  .max(60, "Too long")
  .test("year-range", YEAR_RANGE_MESSAGE, (v) =>
    !v ? true : v.split(",").every((part) => !part.trim() || YEAR_PART.test(part)),
  );

/** Spanish-locale variant for the es POA editors. */
export const yearRangeEs = Yup.string()
  .max(60, "Demasiado largo")
  .test(
    "year-range",
    "Ingrese un año, rango (2021-2023), trimestre (Q1 2022) o N/A",
    (v) => (!v ? true : v.split(",").every((part) => !part.trim() || YEAR_PART.test(part))),
  );

/** Monthly payment day must be a whole number 1–28 (blank allowed). */
export const paymentDay = Yup.string().test(
  "payment-day",
  "Enter a day of the month from 1 to 28",
  (v) => {
    if (!v) return true;
    if (!/^\d+$/.test(v.trim())) return false;
    const n = Number(v);
    return n >= 1 && n <= 28;
  },
);

